import {isBlankString, isValidRegExpPattern, uniq, validateRequired, validateSelectRequired} from './MembersValidation'
import dayjs from 'dayjs'
import {ContractMethod, ContractTerm, CreditCardType} from '../../Props/ContractInputInfo'
import {Optional} from '../../../Common/TypeHelper'

export const validatePaymentInputTerm = (value: ContractTerm, isEdit?: boolean): string[] => {
  if (isEdit) {
    return uniq([
      ...validateSelectRequired(value, '次回契約単位'),
    ])
  }

  return uniq([
    ...validateSelectRequired(value, '契約単位'),
  ])
}

export const validatePaymentInputMethod = (
  inputMethod: Optional<ContractMethod>, isEdit?: boolean,
): string[] => {
  if (!inputMethod) {
    if (isEdit) return uniq(['次回契約のお支払い方法を選択してください。'])
    return uniq([`お支払い方法を選択してください。`])
  }
  return uniq([])
}

export const validateCreditCardType = (
  method: Optional<string>,
  cardType: CreditCardType,
): string[] => {
  if (method !== 'credit_card' && method !== undefined) {
    return []
  }

  return uniq([
    ...validateSelectRequired(cardType, 'クレジットカード'),
  ])
}

export const validateCreditCardNumber = (
  method: Optional<string>,
  type: CreditCardType,
  value: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (type !== 'newCard') {
    return []
  }

  return uniq([
    ...validateRequired(value, 'カード番号'),
    ...validateCreditCardNumberFormat(value),
  ])
}

export const validateCreditCardHolderName = (
  method: Optional<string>,
  type: CreditCardType,
  value: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (type !== 'newCard') {
    return []
  }

  return uniq([
    ...validateRequired(value, '名義人'),
    ...validateCreditCardHolderNameFormat(value),
  ])
}

export const validateCreditCardExpireMonth = (
  method: Optional<string>,
  cardType: CreditCardType,
  value: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (cardType !== 'newCard') {
    return []
  }

  return uniq([
    ...validateSelectRequired(value, '月'),
  ])
}

export const validateCreditCardExpireYear = (
  method: Optional<string>,
  cardType: CreditCardType,
  value: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (cardType !== 'newCard') {
    return []
  }

  return uniq([
    ...validateSelectRequired(value, '年'),
  ])
}

export const validateFutureExpireDate = (
  method: Optional<string>,
  cardType: CreditCardType,
  month: string,
  year: string,
  nextContractStartDate_YYYYMMDD?: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (cardType !== 'newCard') {
    return []
  }
  if (month === '') {
    return []
  }
  if (year === '') {
    return []
  }

  return uniq([
    ...validateFutureDateFormat(month, year, nextContractStartDate_YYYYMMDD),
  ])
}

export const validateCreditCardSecurityCode = (
  method: Optional<string>,
  cardType: CreditCardType,
  value: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (cardType !== 'newCard') {
    return []
  }

  return uniq([
    ...validateRequired(value, 'セキュリティコード'),
    ...validateCreditCardSecurityCodeFormat(value),
  ])
}

export const validateMyCreditCardSecurityCode = (
  method: Optional<string>,
  cardType: CreditCardType,
  value: string,
): string[] => {
  if (method !== 'credit_card') {
    return []
  }
  if (cardType !== 'myCard') {
    return []
  }

  return uniq([
    ...validateRequired(value, 'セキュリティコード'),
    ...validateCreditCardSecurityCodeFormat(value),
  ])
}

export const validateConvenienceStore = (method: Optional<string>, value: string): string[] => {
  if (method !== 'convenience_store') {
    return []
  }

  return uniq([
    ...validateSelectRequired(value, 'コンビニ'),
  ])
}

export const validateMobileCarrier = (method: Optional<string>, value: string): string[] => {
  if (method !== 'mobile_carrier') {
    return []
  }

  return uniq([
    ...validateSelectRequired(value, 'キャリア'),
  ])
}

export const validateCreditCardNumberFormat = (value: string): string[] => {
  if (isBlankString(value)) {
    return []
  }
  if (isValidRegExpPattern(value, /^\d{10,16}$/)) {
    return []
  }
  return ['半角数字10桁〜16桁で入力してください。']
}

export const validateCreditCardHolderNameFormat = (value: string): string[] => {
  if (isBlankString(value)) {
    return []
  }
  if (isValidRegExpPattern(value,
    /^[a-zA-Z \-,./]{3,50}$/g)) {
    return []
  }
  return ['クレジットカードの表記通り半角文字で正確に入力してください。']
}

export const validateCreditCardSecurityCodeFormat = (value: string): string[] => {
  if (isBlankString(value)) {
    return []
  }
  if (isValidRegExpPattern(value, /^\d{3,4}$/)) {
    return []
  }
  return ['半角数字3桁〜4桁で入力してください。']
}

export const validateFutureDateFormat = (
  month: string, year: string, nextContractStartDate_YYYYMMDD?: string,
): string[] => {
  const value = year + month
  const expireDate = dayjs(value).format('YYYY/MM')

  if (!nextContractStartDate_YYYYMMDD) {
    if (expireDate >= dayjs().format('YYYY/MM')) {
      return []
    }

    return ['当月以降の有効期限を選択してください。']
  }

  const [nextStartYear, nextStartMonth] = nextContractStartDate_YYYYMMDD.split('/')
  const nextDateString = `${nextStartYear}/${nextStartMonth}`

  if (expireDate >= dayjs(nextDateString).format('YYYY/MM')) {
    return []
  }

  return ['選択されたカード有効期限は、次回契約お支払い時に有効期限が切れています。利用可能なカード有効期限を選択してください。']
}
